import React, { useState, useRef, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Lock, Search } from "lucide-react";
import { useCategoriesInfinite } from "../../hooks/Workout/useCategories";
import { useWorkoutsInfinite } from "../../hooks/Workout/useWorkouts";
import {
  getBestMedia,
  getFullUrl,
  MEDIA_BASE_URL,
} from "../../Utils/Workout/mediaUtils";
import { BASE_URL } from "../../api/workoutApi";

const FREE_PREVIEW_SECONDS = 90;

// ─── Subscription helper ───────────────────────────────────────────────────
const hasActiveSubscription = () => {
  try {
    const raw = localStorage.getItem("userSubscription");
    if (!raw) return false;
    const sub = JSON.parse(raw);
    if (sub?.status !== "Active") return false;
    if (!sub?.expiryDate) return false;
    return new Date(sub.expiryDate) > new Date();
  } catch {
    return false;
  }
};
// ──────────────────────────────────────────────────────────────────────────

// Workout Card Component
const Workout = ({ workout, categoryName }) => {
  const navigate = useNavigate();

  if (!workout) return null;

  const bestMedia = getBestMedia(workout.media);

  const thumbnailUrl = bestMedia?.thumbnailUrl
    ? getFullUrl(bestMedia.thumbnailUrl)
    : workout?.img
      ? `${MEDIA_BASE_URL}/uploads/workout/${workout.img}`
      : null;

  const handlePlay = () => {
    navigate(`/workout/${workout._id}`);
  };

  return (
    <div className="w-full">
      <div
        className="relative rounded-3xl overflow-hidden bg-gray-200 shadow-lg border border-gray-100 cursor-pointer group"
        style={{ paddingTop: "56.25%" }}
        onClick={handlePlay}
      >
        <div className="absolute inset-0">
          {thumbnailUrl ? (
            <img
              src={thumbnailUrl}
              alt={workout.title || categoryName}
              className="absolute inset-0 w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
              onError={(e) => {
                e.target.onerror = null;
                e.target.style.display = "none";
              }}
            />
          ) : (
            <div className="absolute inset-0 bg-gradient-to-br from-gray-300 to-gray-400 flex items-center justify-center">
              <span className="text-gray-600 text-sm font-medium">
                No preview
              </span>
            </div>
          )}

          <div className="absolute inset-0" />

          {/* Play Button */}
          <div className="absolute bottom-4 left-4 z-10">
            <div className="w-12 h-12 rounded-full bg-[#c8f000] flex items-center justify-center shadow-lg group-hover:scale-110 active:scale-90 transition-transform">
              <svg
                className="w-6 h-6 text-black ml-1"
                fill="currentColor"
                viewBox="0 0 24 24"
              >
                <path d="M8 5v14l11-7z" />
              </svg>
            </div>
          </div>
        </div>
      </div>

      {workout.title && (
        <p className="mt-2 px-1 text-sm font-semibold text-gray-800 truncate">
          {workout.title}
        </p>
      )}
    </div>
  );
};

// Main Search Component
const SearchFile = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const navigate = useNavigate();
  const handleBack = () => navigate(-1);

  const sentinelRef = useRef(null);

  const {
    data: categoriesData,
    isLoading: categoriesLoading,
    isError: categoriesError,
    error: categoriesErr,
    fetchNextPage: fetchNextCategories,
    hasNextPage: hasNextCategories,
    isFetchingNextPage: isFetchingNextCategories,
  } = useCategoriesInfinite(10);

  const {
    data: workoutsData,
    isLoading: workoutsLoading,
    isError: workoutsError,
    error: workoutsErr,
    fetchNextPage: fetchNextWorkouts,
    hasNextPage: hasNextWorkouts,
    isFetchingNextPage: isFetchingNextWorkouts,
  } = useWorkoutsInfinite(10);

  const categories = categoriesData?.pages.flatMap((p) => p.categories) ?? [];
  const workouts = workoutsData?.pages.flatMap((p) => p.workouts) ?? [];

  const isLoading = categoriesLoading || workoutsLoading;
  const isError = categoriesError || workoutsError;
  const errorMessage = categoriesErr?.message || workoutsErr?.message;

  const hasMore = hasNextCategories || hasNextWorkouts;
  const isFetchingMore = isFetchingNextCategories || isFetchingNextWorkouts;

  const loadMore = useCallback(() => {
    if (hasNextCategories) fetchNextCategories();
    if (hasNextWorkouts) fetchNextWorkouts();
  }, [hasNextCategories, hasNextWorkouts, fetchNextCategories, fetchNextWorkouts]);

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !isFetchingMore) {
          loadMore();
        }
      },
      { threshold: 0.1 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasMore, isFetchingMore, loadMore]);

  const workoutsByCategory = workouts.reduce((acc, workout) => {
    const id = workout?.categoryId?._id;
    if (id) {
      if (!acc[id]) acc[id] = [];
      acc[id].push(workout);
    }
    return acc;
  }, {});

  const filteredCategories = searchTerm
    ? categories.filter((cat) =>
        cat.name.toLowerCase().includes(searchTerm.toLowerCase()),
      )
    : categories;

  const allVideos = filteredCategories.flatMap((category) =>
    (workoutsByCategory[category._id] ?? []).map((workout) => ({
      ...workout,
      categoryName: category.name,
    })),
  );

  const hasAnyVideos = allVideos.length > 0;

  if (isLoading) {
    return (
      <div className="min-h-[400px] flex items-center justify-center bg-white">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#c8f000]" />
          <p className="mt-4 text-gray-500">Loading videos...</p>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="min-h-[400px] flex items-center justify-center bg-white">
        <div className="text-center bg-red-50 p-6 rounded-2xl max-w-md">
          <p className="text-red-600 font-semibold mb-2">Something went wrong</p>
          <p className="text-red-400 text-sm mb-4">{errorMessage}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-[#c8f000] text-black font-semibold rounded-full hover:opacity-90 transition"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white font-sans">
      {/* Search Bar */}
      <div className="sticky top-0 z-20 bg-white border-b border-gray-200 px-4 py-4">
        <div className="max-w-2xl mx-auto">
          <div className="flex items-center gap-3">
            <button
              onClick={handleBack}
              className="p-2 hover:bg-gray-100 rounded-full"
            >
              <ArrowLeft className="w-6 h-6 text-gray-700" />
            </button>

            <div className="relative flex-1">
              <input
                type="text"
                placeholder="Search by category name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-4 py-3 pl-12 pr-10 text-gray-900 bg-gray-50 border border-gray-200 rounded-full focus:outline-none focus:border-[#c8f000] focus:ring-2 focus:ring-[#c8f000]/20 transition-all"
              />
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm("")}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2"
                >
                  <svg
                    className="w-5 h-5 text-gray-400 hover:text-gray-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Videos Grid */}
      {hasAnyVideos ? (
        <div className="px-4 md:px-6 py-6 sm:mb-0 mb-20">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
            {allVideos.map((workout) => (
              <div key={workout._id}>
                <Workout workout={workout} categoryName={workout.categoryName} />
              </div>
            ))}
          </div>

          <div ref={sentinelRef} className="h-8 mt-4 flex items-center justify-center">
            {isFetchingMore && (
              <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-[#c8f000]" />
            )}
          </div>
        </div>
      ) : (
        <div className="text-center py-12">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 rounded-full mb-4">
            <svg
              className="w-8 h-8 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
          </div>
          <p className="text-gray-500 text-lg font-medium mb-2">
            {searchTerm ? "No videos found" : "No videos available"}
          </p>
          <p className="text-gray-400 text-sm">
            {searchTerm
              ? `No videos found in categories matching "${searchTerm}"`
              : "There are no videos available in any category"}
          </p>
          {searchTerm && (
            <button
              onClick={() => setSearchTerm("")}
              className="mt-4 px-4 py-2 bg-[#c8f000] text-black font-semibold rounded-full hover:opacity-90 transition"
            >
              Clear Search
            </button>
          )}
        </div>
      )}
    </div>
  );
};

// Workout Preview Component
const WorkoutPreview = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const videoRef = useRef(null);
  const timerRef = useRef(null);

  const [workout, setWorkout] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);

  const [timeWatched, setTimeWatched] = useState(0);
  const [isLocked, setIsLocked] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [videoStartTime, setVideoStartTime] = useState(null);

  // Fetch the single workout by ID
  useEffect(() => {
    if (!id) return;
    setIsLoading(true);
    setFetchError(null);

    fetch(`${BASE_URL}/workouts/${id}`)
      .then(async (res) => {
        const text = await res.text();
        let data;
        try {
          data = JSON.parse(text);
        } catch {
          throw new Error("Expected JSON but got non-JSON response.");
        }
        if (!res.ok) throw new Error(data?.message || "Failed to load workout");
        return data;
      })
      .then((data) => {
        setWorkout(data.workout ?? data);
      })
      .catch((err) => setFetchError(err.message))
      .finally(() => setIsLoading(false));
  }, [id]);

  const bestMedia = workout ? getBestMedia(workout.media) : null;
  const videoUrl = bestMedia?.url ? getFullUrl(bestMedia.url) : null;
  const thumbnailUrl = bestMedia?.thumbnailUrl
    ? getFullUrl(bestMedia.thumbnailUrl)
    : workout?.img
      ? `${MEDIA_BASE_URL}/uploads/workout/${workout.img}`
      : null;

  const categoryName =
    typeof workout?.categoryId === "object"
      ? workout?.categoryId?.name
      : workout?.categoryId;

  // Determine if this video needs the preview gate
  const isPreviewOnly = !hasActiveSubscription() && bestMedia?.isFree === false;

  // Timer-based redirect (mirrors WorkoutDetail logic)
  useEffect(() => {
    if (isPlaying && videoStartTime && isPreviewOnly) {
      clearTimeout(timerRef.current);

      const elapsedSeconds = (Date.now() - videoStartTime) / 1000;
      const remainingSeconds = Math.max(0, FREE_PREVIEW_SECONDS - elapsedSeconds);

      if (remainingSeconds <= 0) {
        navigate("/plan");
      } else {
        timerRef.current = setTimeout(() => {
          navigate("/plan");
        }, remainingSeconds * 1000);
      }
    } else {
      clearTimeout(timerRef.current);
    }

    return () => clearTimeout(timerRef.current);
  }, [isPlaying, videoStartTime, isPreviewOnly, navigate]);

  // Cleanup on unmount
  useEffect(() => {
    return () => clearTimeout(timerRef.current);
  }, []);

  // Video event listeners
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleTimeUpdate = () => {
      const current = Math.floor(video.currentTime);
      setTimeWatched(current);

      // Only enforce 90s cap for preview-only videos
      if (isPreviewOnly && current >= FREE_PREVIEW_SECONDS) {
        video.pause();
        setIsLocked(true);
        setIsPlaying(false);
        clearTimeout(timerRef.current);
      }
    };

    const handlePlay = () => {
      setIsPlaying(true);
      // Record start time on first play
      setVideoStartTime((prev) => prev ?? Date.now());
    };

    const handlePause = () => setIsPlaying(false);

    video.addEventListener("timeupdate", handleTimeUpdate);
    video.addEventListener("play", handlePlay);
    video.addEventListener("pause", handlePause);

    return () => {
      video.removeEventListener("timeupdate", handleTimeUpdate);
      video.removeEventListener("play", handlePlay);
      video.removeEventListener("pause", handlePause);
    };
  }, [videoUrl, isPreviewOnly]);

  const remaining = Math.max(FREE_PREVIEW_SECONDS - timeWatched, 0);
  const progressPercent = Math.min(
    (timeWatched / FREE_PREVIEW_SECONDS) * 100,
    100,
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center bg-white">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#c8f000]" />
          <p className="mt-4 text-gray-500">Loading...</p>
        </div>
      </div>
    );
  }

  if (fetchError || !workout) {
    return (
      <div className="flex items-center justify-center bg-white">
        <div className="text-center">
          <p className="text-gray-500 text-lg">
            {fetchError || "Video not found"}
          </p>
          <button
            onClick={() => navigate(-1)}
            className="mt-4 px-6 py-2 bg-[#c8f000] text-black font-semibold rounded-full"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white font-sans">
      {/* Header */}
      <div className="sticky top-0 z-20 bg-white border-b border-gray-100 px-4 py-3 flex items-center gap-3">
        <button
          onClick={() => navigate(-1)}
          className="p-2 hover:bg-gray-100 rounded-full transition-colors"
        >
          <ArrowLeft className="w-5 h-5 text-gray-700" />
        </button>
        <div className="flex-1 min-w-0">
          <p className="text-xs text-gray-400 font-medium uppercase tracking-wide truncate">
            {categoryName}
          </p>
          {workout.title && (
            <p className="text-sm font-semibold text-gray-800 truncate">
              {workout.title}
            </p>
          )}
        </div>
      </div>

      {/* Video Player */}
      <div className="relative bg-black" style={{ paddingTop: "56.25%" }}>
        <div className="absolute inset-0">
          {videoUrl ? (
            <video
              ref={videoRef}
              className="w-full h-full object-contain"
              poster={thumbnailUrl || undefined}
              playsInline
              controls={!isLocked}
              autoPlay
            >
              <source src={videoUrl} type="application/x-mpegURL" />
              {videoUrl?.endsWith(".m3u8") && (
                <source
                  src={videoUrl.replace(".m3u8", ".mp4")}
                  type="video/mp4"
                />
              )}
            </video>
          ) : (
            thumbnailUrl && (
              <img
                src={thumbnailUrl}
                alt={workout.title || "Video"}
                className="w-full h-full object-cover"
              />
            )
          )}

          {/* Locked Overlay */}
          {isLocked && (
            <div className="absolute inset-0 bg-black/80 flex flex-col items-center justify-center z-10 px-6 text-center">
              <div className="w-16 h-16 rounded-full bg-[#c8f000]/20 border-2 border-[#c8f000] flex items-center justify-center mb-4">
                <Lock className="w-8 h-8 text-[#c8f000]" />
              </div>
              <h3 className="text-white text-xl font-bold mb-2">
                Free Preview Ended
              </h3>
              <p className="text-gray-400 text-sm mb-6 max-w-xs">
                You've watched the 90-second free preview. Subscribe to continue
                watching the full video.
              </p>
              <button
                onClick={() => navigate("/plan")}
                className="px-8 py-3 bg-[#c8f000] text-black font-bold rounded-full text-sm hover:opacity-90 active:scale-95 transition-all shadow-lg"
              >
                Subscribe Now
              </button>
              <button
                onClick={() => navigate(-1)}
                className="mt-3 text-gray-400 text-sm underline underline-offset-2"
              >
                Go Back
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Preview Progress Bar — only for non-subscribed users on locked videos */}
      {isPreviewOnly && !isLocked && (
        <div className="px-4 pt-3 pb-1">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-xs text-gray-400 font-medium">
              Free Preview
            </span>
            <span
              className={`text-xs font-bold ${
                remaining <= 20 ? "text-red-500" : "text-gray-500"
              }`}
            >
              {remaining}s remaining
            </span>
          </div>
          <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all duration-1000 ${
                remaining <= 20 ? "bg-red-400" : "bg-[#c8f000]"
              }`}
              style={{ width: `${progressPercent}%` }}
            />
          </div>
          <p className="text-xs text-gray-400 mt-1.5 text-center">
            Subscribe to watch the full video
          </p>
        </div>
      )}

      {/* Info Section */}
      <div className="px-4 py-4">
        {workout.title && (
          <h2 className="text-lg font-bold text-gray-900 mb-1">
            {workout.title}
          </h2>
        )}
        {categoryName && (
          <span className="inline-block px-3 py-1 bg-[#c8f000]/20 text-gray-700 text-xs font-semibold rounded-full">
            {categoryName}
          </span>
        )}
        {workout.description && (
          <p className="mt-3 text-sm text-gray-500 leading-relaxed">
            {workout.description}
          </p>
        )}
      </div>

      {/* Subscribe CTA — only for non-subscribed users, non-locked state */}
      {isPreviewOnly && !isLocked && (
        <div className="px-4 pb-8">
          <div className="bg-gray-50 rounded-2xl p-4 flex items-center justify-between gap-3">
            <div>
              <p className="text-sm font-semibold text-gray-800">
                Watch full video
              </p>
              <p className="text-xs text-gray-400">
                Get unlimited access to all videos
              </p>
            </div>
            <button
              onClick={() => navigate("/plan")}
              className="shrink-0 px-5 py-2.5 bg-[#c8f000] text-black font-bold rounded-full text-sm hover:opacity-90 active:scale-95 transition-all"
            >
              Subscribe
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export { SearchFile, WorkoutPreview };
export default SearchFile;